/**
 * ActivityLogViewer.jsx — Iter 329 (Jul 2026)
 *
 * Powers Settings → Activity Log. Replaces the old hardcoded April-2026
 * demo array with a live, filterable feed of the `activity_log`
 * collection (via GET /api/admin/activity-log).
 *
 * Filters (all server-side, AND-ed):
 *   • Date range (from / to)
 *   • User (dropdown populated from top-100 actors by activity count)
 *   • Action (dropdown populated from top-60 action types)
 *   • Text search (matches type / details / summary / outcome)
 *   • Show system events toggle (default OFF — auto-hides the ~50k
 *     stage_attempt/task_transition/ai_call_summary/nppes/imported
 *     rows that would otherwise drown out human activity)
 *
 * Pagination: 50 rows per page, "Load more" appends the next 50.
 * Filters preserve the existing `data-testid="settings-activity-log"`
 * wrapper so downstream tests continue to work.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { RefreshCw, Search, X } from "lucide-react";

const PAGE_SIZE = 50;

function _formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return String(iso).slice(0, 19).replace("T", " ");
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return String(iso); }
}

const ACTION_GROUPS = [
  { value: "", label: "All actions" },
  { value: "call",    label: "Calls" },
  { value: "email",   label: "Emails" },
  { value: "sms",     label: "SMS" },
  { value: "stage",   label: "Stage changes" },
  { value: "meeting", label: "Meetings & demos" },
  { value: "lead",    label: "Lead edits & contracts" },
  { value: "note",    label: "Notes" },
  { value: "admin",   label: "Admin / RBAC" },
  { value: "system",  label: "System / imports" },
];

const GROUP_BADGE = {
  call:    { bg: "#DBEAFE", fg: "#1E40AF" },
  email:   { bg: "#EDE9FE", fg: "#5B21B6" },
  sms:     { bg: "#FCE7F3", fg: "#9D174D" },
  stage:   { bg: "#FEF3C7", fg: "#92400E" },
  meeting: { bg: "#D1FAE5", fg: "#065F46" },
  lead:    { bg: "#E0E7FF", fg: "#3730A3" },
  note:    { bg: "#F0FDF4", fg: "#166534" },
  admin:   { bg: "#FEE2E2", fg: "#991B1B" },
  system:  { bg: "#F3F4F6", fg: "#4B5563" },
  other:   { bg: "#F3F4F6", fg: "#4B5563" },
};

export default function ActivityLogViewer() {
  const { token } = useAuth();
  // Filter state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [userId, setUserId] = useState("");
  const [actionGroup, setActionGroup] = useState("");
  const [actionType, setActionType] = useState("");     // specific type overrides group
  const [showSystem, setShowSystem] = useState(false);
  const [search, setSearch] = useState("");
  // Data state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [actionFacets, setActionFacets] = useState([]);
  const [userFacets, setUserFacets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced text-search input mirror so filters don't refire on every keystroke.
  const [searchDraft, setSearchDraft] = useState("");
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(searchDraft), 350);
    return () => clearTimeout(debounceRef.current);
  }, [searchDraft]);

  const fetchPage = useCallback(async ({ append = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const params = {
        limit: PAGE_SIZE,
        skip: append ? skip + PAGE_SIZE : 0,
        show_system: showSystem ? "true" : "false",
      };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (userId) params.user_id = userId;
      if (actionType) params.action_type = actionType;
      else if (actionGroup) params.action_group = actionGroup;
      if (search) params.search = search;
      const r = await axios.get(`${API}/admin/activity-log`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        signal: abortRef.current.signal,
        timeout: 20000,
      });
      const data = r?.data || {};
      const pageRows = data.rows || [];
      if (append) {
        setRows((prev) => [...prev, ...pageRows]);
        setSkip((s) => s + PAGE_SIZE);
      } else {
        setRows(pageRows);
        setSkip(0);
      }
      // A full page => there might be more. A short page => we've reached the tail.
      setLastPageWasFull(pageRows.length >= PAGE_SIZE);
      setTotal(Number(data.total || 0));
      setCapped(Boolean(data.capped));
      // Facets returned regardless of filters — use them to populate dropdowns.
      if (data.filters?.actions) setActionFacets(data.filters.actions);
      if (data.filters?.users)   setUserFacets(data.filters.users);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      const msg = err?.response?.data?.detail || err?.message || "Failed to load activity log";
      toast.error(msg);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [token, startDate, endDate, userId, actionType, actionGroup, showSystem, search, skip]);

  // Refetch on any filter change (skip is intentionally EXCLUDED from
  // this dep list so pagination doesn't loop).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPage({ append: false }); },
    [startDate, endDate, userId, actionType, actionGroup, showSystem, search]);

  // Iter 329.1 — "hasMore" is based on whether the last fetched
  // page returned a full PAGE_SIZE (best-effort "there's likely more")
  // rather than `rows.length < total`. With the 10k count cap this
  // was silently hiding the Load-more button whenever total > 10k
  // (i.e. the default view). Now we always let the rep page forward
  // until the server hands back a short page.
  const [lastPageWasFull, setLastPageWasFull] = useState(false);
  const hasMore = lastPageWasFull;
  const hasActiveFilters = !!(startDate || endDate || userId || actionType || actionGroup || search || showSystem);

  const resetFilters = () => {
    setStartDate(""); setEndDate(""); setUserId("");
    setActionGroup(""); setActionType("");
    setSearchDraft(""); setSearch("");
    setShowSystem(false);
  };

  // Group action facets by group for the "specific type" dropdown.
  const groupedActionFacets = useMemo(() => {
    const grouped = {};
    for (const a of actionFacets) {
      const g = a.group || "other";
      (grouped[g] = grouped[g] || []).push(a);
    }
    return grouped;
  }, [actionFacets]);

  return (
    <div data-testid="activity-log-viewer">
      {/* Toolbar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end", marginBottom: 14, padding: "12px 14px", background: "var(--subtle, #F9FAFB)", border: "0.5px solid var(--border)", borderRadius: 8 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>From</div>
          <input data-testid="al-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                 style={{ padding: "6px 8px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 6, background: "var(--surface, #fff)", fontFamily: "inherit" }} />
        </div>
        <div>
          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>To</div>
          <input data-testid="al-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 style={{ padding: "6px 8px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 6, background: "var(--surface, #fff)", fontFamily: "inherit" }} />
        </div>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>User</div>
          <select data-testid="al-user-filter" value={userId} onChange={(e) => setUserId(e.target.value)}
                  style={{ padding: "6px 8px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 6, background: "var(--surface, #fff)", fontFamily: "inherit", width: "100%" }}>
            <option value="">All users</option>
            {userFacets.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name} ({u.count.toLocaleString()})
              </option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>Action group</div>
          <select data-testid="al-action-group" value={actionGroup} onChange={(e) => { setActionGroup(e.target.value); setActionType(""); }}
                  style={{ padding: "6px 8px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 6, background: "var(--surface, #fff)", fontFamily: "inherit", width: "100%" }}>
            {ACTION_GROUPS.map((g) => (<option key={g.value} value={g.value}>{g.label}</option>))}
          </select>
        </div>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>Specific action</div>
          <select data-testid="al-action-type" value={actionType} onChange={(e) => setActionType(e.target.value)}
                  style={{ padding: "6px 8px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 6, background: "var(--surface, #fff)", fontFamily: "inherit", width: "100%" }}>
            <option value="">Any</option>
            {Object.entries(groupedActionFacets).map(([g, list]) => (
              <optgroup key={g} label={g.charAt(0).toUpperCase() + g.slice(1)}>
                {list.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label} ({a.count.toLocaleString()})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div style={{ minWidth: 200, flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>Search</div>
          <div style={{ position: "relative" }}>
            <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "var(--text-tertiary)" }} />
            <input data-testid="al-search" placeholder="type, outcome, summary…" value={searchDraft}
                   onChange={(e) => setSearchDraft(e.target.value)}
                   style={{ padding: "6px 8px 6px 26px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 6, background: "var(--surface, #fff)", fontFamily: "inherit", width: "100%" }} />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text-secondary)", cursor: "pointer" }}>
            <input data-testid="al-show-system" type="checkbox" checked={showSystem} onChange={(e) => setShowSystem(e.target.checked)} />
            <span>Show system events</span>
          </label>
          <button data-testid="al-reset" type="button" onClick={resetFilters}
                  disabled={!hasActiveFilters}
                  style={{ padding: "5px 10px", fontSize: 11, border: "0.5px solid var(--border)", borderRadius: 6, background: "var(--surface, #fff)", color: hasActiveFilters ? "var(--text-primary)" : "var(--text-tertiary)", cursor: hasActiveFilters ? "pointer" : "not-allowed", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <X style={{ width: 11, height: 11 }} /> Reset
          </button>
        </div>
      </div>

      {/* Result count / status line */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }} data-testid="al-status">
          {loading ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <RefreshCw style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> Loading…
            </span>
          ) : (
            <>
              Showing <strong>{rows.length.toLocaleString()}</strong> of{" "}
              <strong>{capped ? "10,000+" : total.toLocaleString()}</strong> matching entries
              {!showSystem && !actionType && !actionGroup && (
                <span style={{ marginLeft: 8, color: "var(--text-tertiary)", fontStyle: "italic" }}>
                  · system events hidden
                </span>
              )}
            </>
          )}
        </div>
        <button type="button" onClick={() => fetchPage({ append: false })}
                data-testid="al-refresh"
                style={{ padding: "4px 10px", fontSize: 11, border: "0.5px solid var(--border)", borderRadius: 6, background: "var(--surface, #fff)", cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <RefreshCw style={{ width: 11, height: 11 }} /> Refresh
        </button>
      </div>

      {/* Table */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "0.5px solid var(--border)" }}>
            {["Timestamp", "User", "Action", "Details"].map((h) => (
              <th key={h} style={{ padding: "8px 10px", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textAlign: "left", textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !loading && (
            <tr>
              <td colSpan={4} style={{ padding: "24px 10px", textAlign: "center", fontSize: 12, color: "var(--text-tertiary)" }} data-testid="al-empty">
                No activity matches your filters.
              </td>
            </tr>
          )}
          {rows.map((r) => {
            const badge = GROUP_BADGE[r.group] || GROUP_BADGE.other;
            return (
              <tr key={r.id} style={{ borderBottom: "0.5px solid var(--border)" }} data-testid={`al-row-${r.id}`}>
                <td style={{ padding: "8px 10px", fontSize: 11, color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>{_formatWhen(r.timestamp)}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, fontWeight: 520 }}>
                  {r.user_name || "System"}
                  {r.user_role && (
                    <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3, marginTop: 1 }}>
                      {String(r.user_role).replace(/_/g, " ")}
                    </div>
                  )}
                </td>
                <td style={{ padding: "8px 10px", fontSize: 12 }}>
                  <span style={{ display: "inline-block", padding: "2px 7px", borderRadius: 4, background: badge.bg, color: badge.fg, fontSize: 10, fontWeight: 600 }}>
                    {r.action_label}
                  </span>
                </td>
                <td style={{ padding: "8px 10px", fontSize: 11, color: "var(--text-tertiary)" }}>
                  {r.lead_name && <div style={{ fontSize: 11, fontWeight: 500, color: "var(--text-secondary)" }}>{r.lead_name}</div>}
                  {r.details && <div style={{ marginTop: r.lead_name ? 1 : 0 }}>{r.details}</div>}
                  {!r.lead_name && !r.details && <span style={{ opacity: 0.5 }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Load more */}
      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <button type="button" data-testid="al-load-more" onClick={() => fetchPage({ append: true })} disabled={loadingMore}
                  style={{ padding: "8px 20px", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 8, background: loadingMore ? "var(--subtle)" : "var(--surface, #fff)", cursor: loadingMore ? "wait" : "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {loadingMore ? <><RefreshCw style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> Loading…</> : `Load next ${PAGE_SIZE}`}
          </button>
        </div>
      )}
    </div>
  );
}
